import { API_BASE, AUTH_ENABLED } from "../config";
import { normalizeToIsoString } from "../util";
import * as auth from "./auth.js";

const apiBase = API_BASE;

function handleError(err) {
  if (err) console.error(err);
  throw err;
}

function buildUrl(resource) {
  const trimmed = resource.startsWith("/") ? resource.slice(1) : resource;
  return `${apiBase}/${trimmed}`;
}

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : undefined;
};

const addDefinedFields = (target, source = {}) => {
  const updates = Object.fromEntries(
    Object.entries(source).filter(([, value]) => value !== undefined && value !== null)
  );
  return { ...target, ...updates };
};

async function request(url, method, body, extraHeaders) {
  const options = {
    method: method || "GET",
    headers: {},
  };

  if (AUTH_ENABLED) {
    const token = await auth.getToken();
    if (token) {
      options.headers.Authorization = `Bearer ${token}`;
    }
  }

  if (body !== undefined && body !== null) {
    options.body = JSON.stringify(body);
    options.headers["Content-Type"] = "application/json";
  }

  if (extraHeaders) {
    options.headers = { ...options.headers, ...extraHeaders };
  }

  // Always bypass the HTTP cache so users immediately see updated transcripts (e.g., after translation)
  options.cache = "no-store";

  let response;
  try {
    console.debug("Request opts:", JSON.stringify(options, null, 4));
    response = await fetch(url, options);
  } catch (err) {
    return handleError(err);
  }

  console.debug("Response:", response);

  if ((response.status === 403 || response.status === 401) && AUTH_ENABLED) {
    return auth.redirectToLogin("Unauthenticated.");
  }

  if (!response.ok) {
    let errorMessage = `Request failed ${response.status}`;

    try {
      const responseText = await response.text();
      if (responseText) {
        try {
          const parsed = JSON.parse(responseText);
          if (parsed?.error) {
            errorMessage = parsed.error;
          }
        } catch {
          errorMessage = responseText;
        }
      }
    } catch (err) {
      errorMessage = err?.message || errorMessage;
    }

    const error = new Error(errorMessage);
    error.status = response.status;
    return handleError(error);
  }

  if (response.status === 204) return null;

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return response.json();
  }

  return response.text();
}

async function getRequest(resource, data) {
  const url = new URL(buildUrl(resource));

  if (data != null) {
    for (const key in data) {
      url.searchParams.append(key, data[key]);
    }
  }

  return request(url.toString());
}

const extractCallerSentiment = (conversationAnalytics = {}) => {
  const callerLabel =
    conversationAnalytics.SpeakerLabels?.find(
      (labelObj) => labelObj?.DisplayText === "Customer"
    )?.Speaker || "spk_1";

  const sentiment = conversationAnalytics.SentimentTrends?.[callerLabel];

  return {
    callerSentimentScore: sentiment?.SentimentScore,
    callerSentimentChange: sentiment?.SentimentChange,
  };
};

const extractSummaryFields = (conversationAnalytics = {}) => {
  const summary = conversationAnalytics?.Summary;
  const summaryFields = {};

  if (typeof summary === "string") {
    summaryFields.summary_summary = summary;
  } else if (summary && typeof summary === "object") {
    Object.entries(summary).forEach(([key, value]) => {
      const field = `summary_${key.toLowerCase().replace(/\s+/g, "_")}`;
      summaryFields[field] = value;
    });
  }

  const tcaSummary =
    conversationAnalytics?.ContactSummary?.AutoGenerated?.OverallSummary?.Content;
  if (tcaSummary) {
    summaryFields.summary_summary = tcaSummary;
  }

  return summaryFields;
};

const deriveListFieldsFromTranscript = (transcript) => {
  const conversationAnalytics = transcript?.ConversationAnalytics || {};
  const sourceInfo = conversationAnalytics?.SourceInformation?.[0];
  const callMetadata = sourceInfo?.VerbaExtensions?.callMetadata;
  const callStartTime =
    callMetadata?.start_time || conversationAnalytics?.ConversationTime;

  const employeeName =
    `${callMetadata?.FirstName || ""} ${callMetadata?.LastName || ""}`.trim() ||
    undefined;

  const speechSegments = transcript?.SpeechSegments || [];
  const lastSegmentDuration =
    speechSegments.length > 0
      ? speechSegments[speechSegments.length - 1]?.SegmentEndTime
      : undefined;

  const durationSeconds = toNumber(
    conversationAnalytics?.Duration ?? transcript?.duration ?? lastSegmentDuration
  );

  const baseTimestamp = normalizeToIsoString(callStartTime);
  const baseLastModified =
    normalizeToIsoString(
      callStartTime ||
      transcript?.lastModified ||
      transcript?.LastModified ||
      transcript?.uploaded ||
      transcript?.Uploaded
    ) || baseTimestamp;

  return {
    timestamp: baseTimestamp,
    lastModified: baseLastModified,
    jobName: conversationAnalytics?.RecordId,
    agent: conversationAnalytics?.Agent,
    employee: employeeName,
    customer: conversationAnalytics?.Cust,
    guid: conversationAnalytics?.GUID,
    state: callMetadata?.Site,
    lang: conversationAnalytics?.LanguageCode,
    duration: durationSeconds,
    qualityScore: toNumber(callMetadata?.QualityScore),
    ...extractCallerSentiment(conversationAnalytics),
    ...extractSummaryFields(conversationAnalytics),
  };
};

const needsTranscript = (record = {}) =>
  !record.timestamp ||
  record.agent === undefined ||
  record.customer === undefined ||
  record.duration === undefined ||
  record.summary_topic === undefined ||
  record.summary_summary === undefined ||
  record.summary_resolved === undefined ||
  record.summary_product === undefined;

function mapTranscriptListItem(item) {
  const key = item.key || item.Key || item.jobName || "transcript";
  const name = key?.split("/").pop() || key;

  const derivedFields = deriveListFieldsFromTranscript(item);

  const timestampIso =
    derivedFields.timestamp ||
    normalizeToIsoString(
      item?.ConversationAnalytics?.SourceInformation?.[0]?.VerbaExtensions
        ?.callMetadata?.start_time
    ) ||
    normalizeToIsoString(item.timestamp || item.Timestamp);
  const lastModified =
    derivedFields.lastModified ||
    normalizeToIsoString(
      item.lastModified || item.LastModified || item.uploaded || item.Uploaded
    ) ||
    timestampIso;

  const baseRecord = {
    key,
    jobName: derivedFields.jobName || item.jobName || name,
    timestamp: timestampIso,
    status: item.status || "Done",
    guid: derivedFields.guid || item.guid || name,
    agent: item.agent,
    employee: item.employee,
    customer: item.customer,
    queue: item.queue,
    summary_resolved: item.summary_resolved,
    summary_topic: item.summary_topic,
    summary_product: item.summary_product,
    summary_summary: item.summary_summary,
    callerSentimentScore: item.callerSentimentScore,
    callerSentimentChange: item.callerSentimentChange,
    state: item.state,
    lang: item.lang,
    duration: toNumber(item.duration),
    qualityScore: toNumber(item.qualityScore),
    size: item.size,
    lastModified,
  };

  return addDefinedFields(baseRecord, derivedFields);
}

async function ensureStartTime(record) {
  if (!needsTranscript(record)) return record;

  try {
    const transcript = await get(record.key);
    const derivedFields = deriveListFieldsFromTranscript(transcript);

    const updated = addDefinedFields(record, derivedFields);
    if (!updated.lastModified && updated.timestamp) {
      updated.lastModified = updated.timestamp;
    }

    return updated;
  } catch (err) {
    console.error(`Failed to fetch start_time for ${record.key}`, err);
    return record;
  }
}

export async function list(params) {
  const url = new URL(buildUrl("transcripts"));

  if (params?.startKey) url.searchParams.set("cursor", params.startKey);
  if (params?.count) url.searchParams.set("limit", params.count);

  const since = normalizeToIsoString(params?.since ?? params?.timestampFrom);
  const until = normalizeToIsoString(params?.until);

  if (since) url.searchParams.set("since", since);
  if (until) url.searchParams.set("until", until);

  const result = await request(url.toString());

  const records = Array.isArray(result?.objects)
    ? result.objects
      .filter((item) => {
        const objectKey = item.key || item.Key;
        return objectKey && objectKey.endsWith(".json");
      })
      .map(mapTranscriptListItem)
    : Array.isArray(result?.Records)
      ? result.Records.map(mapTranscriptListItem)
      : [];

  const startKey =
    result?.cursor !== undefined
      ? result.cursor
      : result?.StartKey !== undefined
        ? result.StartKey
        : null;

  const recordsWithStartTime = await Promise.all(
    records.map((record) => ensureStartTime(record))
  );

  return {
    Records: recordsWithStartTime,
    StartKey: startKey,
  };
}

export async function getPhoneDirectory() {
  return getRequest("phone-directory");
}

export async function getReportsScores() {
  return getRequest("reports/scores");
}

export async function getAverageScores() {
  return getRequest("reports/scores");
}

export async function updatePhoneDirectory(payload, lastUpdated) {
  return request(buildUrl("phone-directory"), "PUT", payload, {
    "If-Match": lastUpdated,
  });
}

export async function get(key) {
  if (!key) throw new Error("Transcript key is required");
  const url = new URL(buildUrl("transcript"));
  url.searchParams.set("key", key);
  return request(url.toString());
}

export async function head(key) {
  if (!key) throw new Error("Transcript key is required");
  const url = new URL(buildUrl("transcript"));
  url.searchParams.set("key", key);
  return request(url.toString(), "HEAD");
}

export async function search(query) {
  return getRequest("search", query);
}

export async function swap(key) {
  return request(buildUrl(`swap/${key}`), "PUT");
}

export async function markResolved({ keys, username }) {
  const keysArray = Array.isArray(keys) ? keys.filter(Boolean) : [];

  if (keysArray.length === 0) {
    throw new Error("At least one transcript key is required");
  }

  const url = buildUrl("resolve");
  return request(url.toString(), "PUT", { keys: keysArray, username });
}

export async function resolveCalls({ keys, username }) {
  const keysArray = Array.isArray(keys) ? keys.filter(Boolean) : [];

  if (keysArray.length === 0) {
    throw new Error("At least one call key is required");
  }

  const url = buildUrl("resolve");
  return request(url.toString(), "POST", { keys: keysArray, username });
}

export async function entities() {
  return getRequest("entities");
}

export async function translateTranscript(key, sourceLang) {
  if (!key) throw new Error("Transcript key is required");
  const url = buildUrl("translate");
  return request(url.toString(), "POST", {
    key,
    sourceLang,
  });
}

export async function revertTranscript(key) {
  if (!key) throw new Error("Transcript key is required");
  const url = buildUrl("translate/revert");
  return request(url.toString(), "POST", { key });
}

export async function languages() {
  return getRequest("languages");
}

export async function genaiquery(filename, query) {
  return getRequest(`genaiquery`, {
    filename: filename,
    query: query,
  });
}

export async function genairefresh(filename) {
  return getRequest("genai/refreshsummary", {
    filename: filename,
  });
}

export async function listNotes(key) {
  if (!key) throw new Error("Transcript key is required");
  return getRequest("notes", { key });
}

export async function createNote({ key, content, username }) {
  if (!key) throw new Error("Transcript key is required");
  return request(buildUrl("notes"), "POST", {
    key,
    content,
    username,
  });
}

export async function presign(filename) {
  return getRequest("presign", {
    filename: filename,
  });
}
